/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useLayoutEffect, useState } from 'react';

import { jsx } from '@atlaskit/css';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { fg } from '@atlaskit/platform-feature-flags';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react';

import {
	ContentComponentContextProvider,
	type ContentComponentProps,
} from '../../hooks/useContentComponentContext';
import { GlobalWrapper } from '../GlobalWrapper/GlobalWrapper';
import { SessionUIComponent } from '../SessionUI/SessionUIComponent';

import { ContentComponent } from './ContentComponent';

const useSharedPluginStateNoDebounce = (api: ContentComponentProps['api']) => {
	const [state, setState] = useState(
		api?.aiExperience?.sharedState.currentState()?.aiDecorationExperiencePluginState
			.modalMountedTimeStamp,
	);
	useLayoutEffect(() => {
		const unsub = api?.aiExperience?.sharedState.onChange(
			({ prevSharedState, nextSharedState }) => {
				if (
					prevSharedState?.aiDecorationExperiencePluginState.modalMountedTimeStamp !==
					nextSharedState?.aiDecorationExperiencePluginState.modalMountedTimeStamp
				) {
					setState(nextSharedState?.aiDecorationExperiencePluginState.modalMountedTimeStamp);
				}
			},
		);

		return () => {
			unsub?.();
		};
	}, [api]);

	return state;
};

const useModalMountedTimestamp = conditionalHooksFactory(
	() => fg('aifc_create_enabled'),
	(api: ContentComponentProps['api']) => useSharedPluginStateNoDebounce(api),
	(api: ContentComponentProps['api']) => {
		const aiDecorationExperiencePluginState = useSharedPluginStateSelector(
			api,
			'aiExperience.aiDecorationExperiencePluginState',
		);
		return aiDecorationExperiencePluginState?.modalMountedTimeStamp;
	},
);
export const ContentComponentWrapper = (
	props: Omit<ContentComponentProps, 'isRovoLoading' | 'setIsRovoLoading'>,
) => {
	const modalMountedTimeStamp = useModalMountedTimestamp(props.api);

	/**
	 * This is to detect the state of the Rovo prompt and loading screens
	 * from RovoInlineChatPrompt used in RovoPromptScreenWithLogic,
	 * as it does not respect the state value from XState.
	 */
	const [isRovoLoading, setIsRovoLoading] = useState(false);

	/* reset the component when start AI again, so that the error boundary will also be reset */
	return (
		<GlobalWrapper>
			<ContentComponentContextProvider value={{ ...props, isRovoLoading, setIsRovoLoading }}>
				<ContentComponent key={modalMountedTimeStamp} />
				{fg('platform_editor_ai_aifc_patch_beta_2') && (
					<SessionUIComponent
						api={props.api}
						providerFactory={props.providerFactory}
						appearance={props.appearance}
					/>
				)}
			</ContentComponentContextProvider>
		</GlobalWrapper>
	);
};
